import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnyAdapter } from '@drl/core/api/adapters/any-adapter.service';
import { ApiService } from '@drl/core/api/api.service';
import { Count } from '@drl/core/models';
import { environment } from '@drl/env/environment';
import { AppointmentsAdapter } from '@drl/shared/adapters/appointments-adapter.service';
import { GetAppointmentsCountCommand } from '@drl/shared/commands/get-appointments-count.command';
import { GetAppointmentsCommand } from '@drl/shared/commands/get-appointments.command';
import { UpdateAppointmentsCommand } from '@drl/shared/commands/update-appointment.command';
import { Appointment, Appointments } from '@drl/shared/models';
import { AppointmentType } from '@drl/shared/utility/appointment-type.enum';
import { Observable, of } from 'rxjs';

import { CommonResponseAppointmentModel } from './types';

@Injectable()
export class EventFacadeService {
  readonly BaseIntURI = environment.externalIntApiUrl;
  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
    private readonly appointmentAdapter: AppointmentsAdapter,
    private readonly http: HttpClient,
  ) { }

  getAllAppointments(
    where?: {
      startDate?: Date;
      endDate?: Date;
      apptType?: AppointmentType;
      venue?: object;
    },
    patientIds?: number[],
  ) {
    const command: GetAppointmentsCommand<Appointments> = new GetAppointmentsCommand(
      this.apiService,
      this.appointmentAdapter,
    );
    const order = [];
    const queryData = {
      and: [],
      patientId: undefined,
    };
    if (where) {
      if (where.startDate) {
        queryData.and.push({
          apptDate: {
            gte: where.startDate,
          },
        });
      }
      if (where.endDate) {
        queryData.and.push({
          apptDate: {
            lte: where.endDate,
          },
        });
      }
      if (where.apptType) {
        queryData.and.push({
          apptType: where.apptType,
        });
      }
      if (where.venue) {
        queryData.and.push({
          venue: where.venue,
        });
      }
    }
    if (patientIds && patientIds.length) {
      queryData.patientId = {
        inq: patientIds,
      };
    } else if (patientIds && !patientIds.length) {
      // if no patient associated with selected care group then
      return of([]);
    } else {
      // do nothing
    }
    const queryStr = JSON.stringify({
      where: queryData,
      order,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };
    return command.execute();
  }

  getAllAppointmentsNew(body): Observable<CommonResponseAppointmentModel> {
    return this.http.post<CommonResponseAppointmentModel>(
      this.BaseIntURI + '/appointment/listAppointmentDetails',
      body,
    );
  }
  // Changed get Appointments new
  getAllAppointmentsOnline(
    where?: {
      startDate?: Date;
      endDate?: Date;
      apptType?: AppointmentType;
      venue?: object;
    },
    patientIds?: number[],
  ) {
    const command: GetAppointmentsCommand<Appointments> = new GetAppointmentsCommand(
      this.apiService,
      this.appointmentAdapter,
    );
    const order = [];
    const queryData = {
      and: [],
      patientId: undefined,
    };
    if (where) {
      if (where.startDate) {
        queryData.and.push({
          apptDate: {
            gte: where.startDate,
          },
        });
      }
      if (where.endDate) {
        queryData.and.push({
          apptDate: {
            lte: where.endDate,
          },
        });
      }
      if (where.apptType) {
        queryData.and.push({
          apptType: where.apptType,
        });
      }
      if (where.venue) {
        queryData.and.push({
          venue: where.venue,
        });
      }
    }
    if (patientIds && patientIds.length) {
      queryData.patientId = {
        inq: patientIds,
      };
    } else if (patientIds && !patientIds.length) {
      // if no patient associated with selected care group then
      return of([]);
    } else {
      // do nothing
    }
    const queryStr = JSON.stringify({
      where: queryData,
      order,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };
    return command.execute();
  }
  //

  getAllAppointmentsCount(body): Observable<CommonResponseAppointmentModel> {
    return this.http.post<CommonResponseAppointmentModel>(
      this.BaseIntURI + '/appointment/listAppointmentDetailsCount',
      body,
    );
  }

  getAppointmentsCount(
    where?: {
      startDate?: Date;
      endDate?: Date;
      apptType?: AppointmentType;
      venue?: object;
    },
    patientIds?: number[],
  ) {
    const command: GetAppointmentsCountCommand<Count> = new GetAppointmentsCountCommand(
      this.apiService,
      this.anyAdapter,
    );
    const queryData = {
      apptDate: undefined,
      apptType: undefined,
      patientId: undefined,
      venue: undefined,
    };
    if (where.startDate && where.endDate) {
      queryData.apptDate = {
        between: [where.startDate, where.endDate],
      };
    }
    if (where.apptType) {
      queryData.apptType = where.apptType;
    }

    if (where.venue) {
      queryData.venue = where.venue;
    }

    if (patientIds && patientIds.length) {
      queryData.patientId = {
        inq: patientIds,
      };
    } else if (patientIds && !patientIds.length) {
      // if no patient associated with selected care group then
      return of(new Count());
    } else {
      // do nothing
    }

    const queryStr = JSON.stringify(queryData);
    command.parameters = {
      query: new HttpParams().set('where', queryStr),
    };

    return command.execute();
  }

  updateAppointment(appointment: Partial<Appointment>) {
    const command: UpdateAppointmentsCommand<Appointment> = new UpdateAppointmentsCommand(
      this.apiService,
      this.anyAdapter,
      appointment.patientId,
      appointment.id,
    );

    command.parameters = {
      data: appointment,
    };

    return command.execute();
  }
}
